/**
 * CanvasHost — CANVAS-SHELL-1 (01/10/2026): superfície do VUKZ Canvas.
 *
 * Registry `kind → componente`. Nesta tranche só o placeholder genérico;
 * WellnessCanvas / TrainingDraftCanvas entram nas próximas, registrando-se
 * aqui (o modelo nunca gera JSX — `open_canvas` → descriptor → registry).
 *
 * O host NÃO busca dados nem conhece wellness/training: recebe o descriptor
 * do AssistContext e delega. O componente registrado é quem autoriza e
 * refaz o fetch a partir do descriptor (nunca de dados persistidos).
 */
import React, { Suspense } from 'react';
import { LayoutPanelLeft, X, Loader2, FileEdit } from 'lucide-react';
import { useAssist } from '../../contexts/AssistContext';

const KIND_LABELS = { wellness: 'Wellness', training_draft: 'Rascunho de Treino', training_created: 'Treino criado', training_edit: 'Editando treino' };

// Registry — kinds lazy (o host não conhece wellness/training por dentro).
const CANVAS_REGISTRY = {
  wellness: React.lazy(() => import('../wellness/WellnessCanvas')),
  // CANVAS-ENGINE-1: observa o Draft C3 (mesma fonte do Builder)
  training_draft: React.lazy(() => import('../training/TrainingDraftCanvas')),
  // CANVAS-COMPLETE-1: fechamento — plano canônico criado a partir do Draft
  training_created: React.lazy(() => import('../training/TrainingCreatedCanvas')),
  // EDIT-CANVAS-1: working copy + diff derivado pelo backend (não grava)
  training_edit: React.lazy(() => import('../training/TrainingEditCanvas')),
};

export function registerCanvasKind(kind, Component) {
  CANVAS_REGISTRY[kind] = Component;
}

const EmptyCanvas = () => (
  <div
    data-testid="canvas-empty"
    className="h-full flex flex-col items-center justify-center text-center px-6 text-gray-400"
  >
    <LayoutPanelLeft size={28} className="mb-3 text-gray-300" />
    <p className="text-sm font-semibold text-gray-500">Nenhum conteúdo aberto</p>
    <p className="text-xs mt-1 max-w-[240px]">
      Peça ao Assist para abrir o wellness de um atleta ou um rascunho de treino.
    </p>
  </div>
);

const UnregisteredKind = ({ kind }) => (
  <div data-testid="canvas-unregistered" className="h-full flex items-center justify-center text-xs text-gray-400 px-6 text-center">
    Canvas “{KIND_LABELS[kind] || kind}” ainda não disponível nesta versão.
  </div>
);

const CanvasHost = () => {
  const { canvas, closeCanvas, workspaceLive, sendFromCanvas } = useAssist();
  const Registered = canvas.kind ? CANVAS_REGISTRY[canvas.kind] : null;
  // COACH-CONTEXT-3: visible canvas ≠ draft. Uma leitura (wellness…) pode
  // ocupar o palco enquanto o rascunho de treino segue salvo em segundo plano.
  const bg = canvas.isOpen && !['training_draft', 'training_edit'].includes(canvas.kind) ? workspaceLive?.background_draft : null;
  const bgWho = bg?.subject?.label || 'atleta';

  return (
    <div data-testid="canvas-host" className="h-full flex flex-col">
      {/* CANVAS-LAYOUT-2: header do palco central — "VUKZ Canvas · <kind>  [×]".
          O × fecha o Canvas e revela a rota que estava atrás (montada). */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 shrink-0">
        <span className="text-xs font-bold text-gray-500 flex items-center gap-1.5">
          <LayoutPanelLeft size={13} className="text-emerald-700" />
          VUKZ Canvas
          {canvas.kind && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-gray-700">{KIND_LABELS[canvas.kind] || canvas.kind}</span>
            </>
          )}
        </span>
        {canvas.isOpen && (
          <button
            onClick={closeCanvas}
            data-testid="canvas-close"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
            title="Fechar Canvas — volta para a página"
          >
            <X size={14} className="text-gray-400" />
          </button>
        )}
      </div>
      {bg && (
        <div
          data-testid="canvas-background-draft"
          data-revision={bg.revision ?? ''}
          className="flex items-center justify-between gap-2 px-4 py-1.5 border-b text-[11px] shrink-0"
          style={{ backgroundColor: '#ecfdf5', borderColor: '#d1fae5', color: '#064e3b' }}
        >
          <span className="flex items-center gap-1.5 truncate">
            <FileEdit size={12} />
            Rascunho de treino de <strong>{bgWho}</strong> salvo em segundo plano
          </span>
          <button
            type="button"
            data-testid="canvas-background-draft-resume"
            onClick={() => sendFromCanvas && sendFromCanvas(`volta pro treino de ${bgWho}`)}
            className="font-bold underline shrink-0"
          >
            Voltar ao rascunho
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-5xl mx-auto h-full">
          {!canvas.isOpen || !canvas.kind
            ? <EmptyCanvas />
            : Registered
              ? (
                <Suspense fallback={<div className="h-full flex items-center justify-center text-gray-400"><Loader2 size={18} className="animate-spin" /></div>}>
                  <Registered descriptor={canvas} />
                </Suspense>
              )
              : <UnregisteredKind kind={canvas.kind} />}
        </div>
      </div>
    </div>
  );
};

export default CanvasHost;
